import React, { useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Badge, Box, Button, Container, CssBaseline, Link, ThemeProvider, Typography, createTheme } from '@mui/material';
import { useBranding } from '../../contexts/BrandingContext';
import type { PublicStorefront } from '../../services/storefrontService';
import { resolveStorefrontBranding } from './storefrontHelpers';
import { useStorefrontT } from './useStorefrontT';
import MaterialSymbol from '../common/MaterialSymbol';
import { tokens } from '../../designTokens';

interface Props {
    slug: string;
    storefront: PublicStorefront | null;
    cartCount: number;
    pageTitle?: string;
    children: React.ReactNode;
}

/**
 * Eigener Rahmen der öffentlichen Storefront (Plan 3.8): ohne App-Navigation,
 * mit Mandanten-Branding (BrandingContext bzw. gespiegeltes Branding aus der
 * Storefront-API), SEO-Titel/Meta und optionalem Mandanten-CSS.
 */
const StorefrontLayout: React.FC<Props> = ({ slug, storefront, cartCount, pageTitle, children }) => {
    const { t } = useStorefrontT();
    const { branding, companyName } = useBranding();
    const resolved = useMemo(() => resolveStorefrontBranding(branding, storefront?.branding), [branding, storefront]);
    const theme = useMemo(
        () =>
            createTheme({
                palette: { primary: { main: resolved.primaryColor }, secondary: { main: resolved.secondaryColor } },
                // Design-Tokens statt MUI-Defaults (sonst Serifen-Schrift und eine Hero-Zeile in 54 px):
                typography: {
                    fontFamily: tokens.typography.fontFamily,
                    htmlFontSize: tokens.typography.htmlFontSizePx,
                    h1: { ...tokens.typography.h1 },
                    h2: { ...tokens.typography.h2 },
                    h3: { ...tokens.typography.h3 },
                    h4: { ...tokens.typography.h4 },
                    h5: { ...tokens.typography.h5 },
                    h6: { ...tokens.typography.h6 },
                    body1: { ...tokens.typography.body1 },
                    button: { fontWeight: tokens.typography.button.fontWeight, textTransform: tokens.typography.button.textTransform },
                },
            }),
        [resolved],
    );
    const name = storefront?.displayName || companyName;

    useEffect(() => {
        if (!storefront) return;
        document.title = pageTitle ? `${pageTitle} – ${name}` : storefront.seoTitle || name || 'Storefront';
        if (storefront.seoDescription) {
            let meta = document.querySelector('meta[name="description"]');
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('name', 'description');
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', storefront.seoDescription);
        }
    }, [storefront, pageTitle, name]);

    const base = `/store/${slug}`;
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {storefront?.customCss ? <style data-testid="storefront-custom-css">{storefront.customCss}</style> : null}
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'var(--oa-bg, #fff)' }}>
                <Box component="header" sx={{ borderBottom: '1px solid var(--oa-divider, #dee2e6)', py: 1.5 }}>
                    <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Link component={RouterLink} to={base} underline="none" color="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 'auto', minHeight: 'var(--oa-touch-target, 40px)' }}>
                            {resolved.logoUrl ? (
                                <Box component="img" src={resolved.logoUrl} alt={name ?? ''} sx={{ height: 36, maxWidth: 160, objectFit: 'contain' }} />
                            ) : null}
                            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>{name}</Typography>
                        </Link>
                        <Button component={RouterLink} to={`${base}/catalog`} color="inherit">{t('nav.catalog')}</Button>
                        <Button component={RouterLink} to={`${base}/cart`} color="primary" aria-label={t('nav.cart')} startIcon={
                            <Badge badgeContent={cartCount} color="primary" invisible={cartCount === 0}>
                                <MaterialSymbol name="shopping_cart" />
                            </Badge>
                        }>
                            {t('nav.cart')}
                        </Button>
                    </Container>
                </Box>
                <Container component="main" maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
                    {children}
                </Container>
                <Box component="footer" sx={{ borderTop: '1px solid var(--oa-divider, #dee2e6)', py: 2 }}>
                    <Container maxWidth="lg" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                        {storefront?.termsUrl ? <Link href={storefront.termsUrl} target="_blank" rel="noopener noreferrer" color="inherit">{t('footer.terms')}</Link> : null}
                        {storefront?.privacyPolicyUrl ? <Link href={storefront.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" color="inherit">{t('footer.privacy')}</Link> : null}
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>{t('footer.poweredBy')}</Typography>
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default StorefrontLayout;
